import { useState } from 'react'

import { Clock } from '@/components/icons'
import { Button } from '@/components/ui'
import { getSessionAuthority } from '@/trpc/sessionAuthority'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.badland.ai'

/** What a sign-in refused with ACCOUNT_PENDING_DELETION carries (tRPC `error.data.pendingDeletion`). */
export interface PendingDeletion {
  deleteAfter?: string
  restoreToken?: string
  /** The server's sentence, when it came back in a redirect instead of an error body. */
  message?: string
  /** A Google sign-in left the restore token in the restore route's HttpOnly cookie. */
  restoreWithCookie?: boolean
}

/** What a Google sign-in redirect leaves in `/login?error=ACCOUNT_PENDING_DELETION&message=…`. */
export function readPendingDeletionRedirect(search: URLSearchParams): PendingDeletion | null {
  if (search.get('error') !== 'ACCOUNT_PENDING_DELETION') return null
  const message = search.get('message')
  return { ...(message ? { message } : {}), restoreWithCookie: true }
}

/** The pending-deletion details on a tRPC error, if it carries any. */
export function readPendingDeletion(error: unknown): PendingDeletion | null {
  const data = (error as { data?: { pendingDeletion?: unknown } } | null)?.data?.pendingDeletion
  if (!data || typeof data !== 'object') return null
  const { deleteAfter, restoreToken } = data as Record<string, unknown>
  return {
    ...(typeof deleteAfter === 'string' ? { deleteAfter } : {}),
    ...(typeof restoreToken === 'string' ? { restoreToken } : {}),
  }
}

function formatDate(iso: string | undefined): string | null {
  const date = iso ? new Date(iso) : null
  if (!date || Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

interface PendingDeletionNoticeProps {
  pending: PendingDeletion
  /** Called once the account is restored and signed in. */
  onRestored: () => void
  /** Keep the deletion (or, without a token, go back to sign in). */
  onDismiss: () => void
}

/**
 * Signing in to an account inside its 30-day deletion window
 * (account-deletion v4): the date, Restore account and Keep deletion, the same
 * choice the web app and the iPhone app offer.
 */
export function PendingDeletionNotice({ pending, onRestored, onDismiss }: PendingDeletionNoticeProps) {
  const [restoring, setRestoring] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const date = formatDate(pending.deleteAfter)
  const canRestore = Boolean(pending.restoreToken || pending.restoreWithCookie)

  async function restore() {
    if (!canRestore) return
    setRestoring(true)
    setError(null)
    try {
      let receivedResponse: Response | undefined
      const response = await getSessionAuthority(API_BASE).write(async advanceEpoch => {
        advanceEpoch('pending')
        try {
          receivedResponse = await fetch(`${API_BASE}/api/auth/account/restore`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(pending.restoreToken ? { restoreToken: pending.restoreToken } : {}),
          })
          return receivedResponse
        } finally {
          advanceEpoch()
        }
      }).catch(error => {
        // A settlement storage error cannot undo a completed server restore.
        if (receivedResponse) return receivedResponse
        throw error
      })
      const body = await response.json().catch(() => ({})) as { success?: boolean; error?: string }
      if (!response.ok || !body.success) {
        setError(body.error || 'Restoring the account failed. Please sign in again.')
        return
      }
      onRestored()
    } catch (error) {
      setError(error instanceof TypeError
        ? 'Could not reach the server. Check your connection and try restoring again.'
        : error instanceof Error && error.message ? error.message : 'Restoring the account failed. Please sign in again.')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="space-y-4 text-left" role="alert">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-warning)/0.12)] text-[rgb(var(--color-text-warning))] ring-1 ring-[rgb(var(--color-warning)/0.3)]">
          <Clock size={18} />
        </span>
        <div className="min-w-0 space-y-1">
          <h2 className="font-[family-name:var(--bl-font-display)] text-2xl font-bold uppercase leading-none tracking-[0.03em] text-[color:var(--bl-ink)]">Account scheduled for deletion</h2>
          <p className="text-sm text-[color:var(--bl-ink-2)]">
            {date ? `This account is scheduled for deletion on ${date}.` : pending.message ?? 'This account is scheduled for deletion.'}
          </p>
        </div>
      </div>
      <p className="rounded-[var(--radius-md)] border border-[color:var(--bl-line)] bg-[color:var(--bl-sunken)] px-3.5 py-3 text-xs leading-relaxed text-[color:var(--bl-ink-2)]">
        Restore it to keep your conversations and your agent. A cancelled subscription stays
        cancelled; you can subscribe again after restoring.
      </p>
      {error ? <p className="text-xs text-[rgb(var(--color-text-error))]">{error}</p> : null}
      {canRestore ? (
        <Button fullWidth size="lg" onClick={() => void restore()} disabled={restoring}>
          {restoring ? 'Restoring…' : 'Restore account'}
        </Button>
      ) : null}
      <Button fullWidth size="lg" variant="secondary" onClick={onDismiss} disabled={restoring}>
        {canRestore ? 'Keep deletion' : 'Sign in to restore it'}
      </Button>
    </div>
  )
}
